/**
 * useSessionLogin Hook - Session-Based Authentication
 *
 * WHY THIS ARCHITECTURE:
 * Alternative login method using session tokens instead of
 * username/password. Used for SSO flows, passwordless login,
 * and session restoration after token refresh.
 *
 * BUSINESS RULES:
 * - Session tokens expire after 15 minutes (security)
 * - Single-use tokens (consumed on successful login)
 * - Cannot be used if MFA enabled (requires full login)
 * - Primarily for trusted authentication flows
 *
 * SECURITY RATIONALE:
 * - Short-lived tokens reduce attack window
 * - Single-use prevents replay attacks
 * - Generated server-side only for trusted flows
 * - No password transmitted over network
 * - Token includes IP and user-agent binding
 *
 * USE CASES:
 * 1. SSO callback - Login after OAuth provider redirect
 * 2. Magic link - Passwordless email authentication
 * 3. Session restoration - After token refresh failure
 * 4. Account linking - Connect social accounts
 *
 * TOKEN STRUCTURE:
 * Session tokens are opaque strings containing:
 * - User identifier
 * - Expiration timestamp
 * - IP address binding
 * - User agent fingerprint
 * - HMAC signature
 *
 * WHY SEPARATE FROM useLogin:
 * Different security model and validation requirements.
 * Session tokens bypass password validation but require
 * server-side token generation and validation.
 *
 * @see apiAuth.sessionLogin - Backend token validation
 * @see useLogin - Standard username/password login
 */
import { useMutation } from "@tanstack/react-query";
import { sessionLogin as loginApi } from "../../services/apiAuth";

// import { useSessionAnalytics } from "../../context/SessionContext";
// WHY COMMENTED: Analytics integration pending
// Will track session login methods for security auditing

/**
 * Manages session token-based authentication.
 *
 * WHY NO CACHE INVALIDATION:
 * Unlike useLogin, session login doesn't need to invalidate
 * queries because it's typically used for initial authentication,
 * not re-authentication. The component handles navigation
 * and any necessary data fetching after successful login.
 *
 * WHY NO SUCCESS CALLBACK:
 * Different session login flows require different handling:
 * - SSO: Redirect to originally requested page
 * - Magic link: Show welcome message
 * - Session restore: Transparent continuation
 * Component handles the appropriate response.
 *
 * @returns {Object} sessionLogin mutation and loading state
 */
export function useSessionLogin() {
  const { mutate: sessionLogin, isLoading } = useMutation({
    mutationFn: loginApi,
    // WHY: Minimal design for maximum flexibility
    // Each use case needs different post-login behavior
  });

  return { sessionLogin, isLoading };
}
